import React, { useState } from 'react';
import { Currency } from '../types';
import { useCurrency } from '../contexts/CurrencyContext';
import { useLanguage } from '../contexts/LanguageContext';
import { DollarSign, ChevronDown } from 'lucide-react';
import { toast } from 'react-toastify';

const CURRENCY_FLAGS: Record<Currency, string> = {
  [Currency.USD]: '🇺🇸',
  [Currency.EUR]: '🇪🇺',
  [Currency.MXN]: '🇲🇽',
};

const CURRENCY_NAMES: Record<Currency, { es: string; en: string }> = {
  [Currency.USD]: { es: 'Dólar Estadounidense', en: 'US Dollar' },
  [Currency.EUR]: { es: 'Euro', en: 'Euro' },
  [Currency.MXN]: { es: 'Peso Mexicano', en: 'Mexican Peso' },
};

interface CurrencySelectorProps {
  className?: string;
  showLabel?: boolean;
  compact?: boolean;
}

const CurrencySelector: React.FC<CurrencySelectorProps> = ({
  className = '',
  showLabel = true,
  compact = false
}) => {
  const { currency, changeCurrency } = useCurrency();
  const { language, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const handleCurrencyChange = async (newCurrency: Currency) => {
    if (newCurrency !== currency) {
      await changeCurrency(newCurrency);
      toast.success(
        language === 'es'
          ? `Moneda cambiada a ${CURRENCY_NAMES[newCurrency].es}`
          : `Currency changed to ${CURRENCY_NAMES[newCurrency].en}`,
        { position: 'top-center', autoClose: 2000 }
      );
    }
    setIsOpen(false);
  };

  const getCurrencyName = (curr: Currency) => {
    return language === 'es' ? CURRENCY_NAMES[curr].es : CURRENCY_NAMES[curr].en;
  };

  if (compact) {
    return (
      <div className={`relative ${className}`}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <span className="text-lg">{CURRENCY_FLAGS[currency]}</span>
          <span className="font-medium">{currency}</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />
            <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
              {Object.values(Currency).map((curr) => (
                <button
                  key={curr}
                  onClick={() => handleCurrencyChange(curr)}
                  className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${
                    curr === currency ? 'bg-blue-50' : ''
                  } first:rounded-t-lg last:rounded-b-lg`}
                >
                  <span className="text-xl">{CURRENCY_FLAGS[curr]}</span>
                  <div className="flex-1 text-left">
                    <div className="font-medium">{curr}</div>
                    <div className="text-sm text-gray-600">{getCurrencyName(curr)}</div>
                  </div>
                  {curr === currency && (
                    <div className="w-2 h-2 bg-blue-600 rounded-full" />
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      {showLabel && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <DollarSign className="inline w-4 h-4 mr-1" />
          {t('currency_preference') || (language === 'es' ? 'Moneda Preferida' : 'Preferred Currency')}
        </label>
      )}

      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-white border-2 border-gray-300 rounded-lg hover:border-blue-500 transition-colors"
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">{CURRENCY_FLAGS[currency]}</span>
            <div className="text-left">
              <div className="font-semibold text-lg">{currency}</div>
              <div className="text-sm text-gray-600">{getCurrencyName(currency)}</div>
            </div>
          </div>
          <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />
            <div className="absolute left-0 right-0 mt-2 bg-white border-2 border-gray-200 rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
              {Object.values(Currency).map((curr) => (
                <button
                  key={curr}
                  onClick={() => handleCurrencyChange(curr)}
                  className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors ${
                    curr === currency ? 'bg-blue-100' : ''
                  } first:rounded-t-lg last:rounded-b-lg`}
                >
                  <span className="text-2xl">{CURRENCY_FLAGS[curr]}</span>
                  <div className="flex-1 text-left">
                    <div className="font-semibold">{curr}</div>
                    <div className="text-sm text-gray-600">{getCurrencyName(curr)}</div>
                  </div>
                  {curr === currency && (
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-blue-600 rounded-full" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <p className="mt-2 text-xs text-gray-500">
        {language === 'es'
          ? 'Los precios y cargos se procesarán en la moneda seleccionada.'
          : 'Prices and charges will be processed in the selected currency.'}
      </p>
    </div>
  );
};

export default CurrencySelector;